/* globals
    $,
    Cookies,
    rscpCfg,
    Control,
    Debug
    ConvertIso8601TimeSpanToHundredths,
    GetErrorDetailString,
    GetCurrentConfigurationJs,
    MergeQueryStringParameters,
    DispatchDriver,
    RESULT_UNKNOWN,
    LAUNCH_TYPE_FRAMESET,
    LOOKAHEAD_SEQUENCER_MODE_DISABLE,
    PREFER_SEND_BEACON
*/
/* eslint-disable camelcase, new-cap */

// eslint-disable-next-line no-unused-vars
function Communications () {
    // internal state data
    this.IntervalFunctionID = "";
    this.IntervalWatchFunctionID = "";
    this.RequestTimeoutIntervalID = "";
    this.FinalExitCalls = 0;
    this.FailedSubmissions = 0;
    this.SequenceNumber = rscpCfg.get("registrationToDeliver").LastSequenceNumber || 0;

    // when disabled, this class does not communicate with the server
    // (used when there is an AICC or cmi5 course launched)
    this.Disabled = false;

    // default to a min of 5 seconds between comms
    this.MinimumCommitFrequency = 5000;

    // default to a max of 5 minutes between comms
    this.MaxCommitFrequency = 300000;

    // warn after a max of 30 minutes of no comms
    this.MaxNonCommitInterval = 1800000;

    // Time at which we assume a pending request has timed out. It is possible
    // that a request will not timeout in a timely manner. If this happens we
    // shouldn't keep sending requests on the commit frequency and letting them
    // potentially pile up. So we apply logic/on the timing of subsequent retries
    // as if it has failed. The 99.9th percentile for requests to engine hovers
    // around 8 seconds, but realistically sometimes spikes up to near 30 seconds
    // (data from Cloud). 30s seems reasonable here considering similar times for
    // default timeouts using Hikari CP.
    this.PendingRequestTimeout = 30000;

    // the maximum amount of time we want to backoff a postback retry. This is the
    // max time we can possibly wait. With this set to 30s it would take 10 failed
    // retries before there was even a chance of waiting this long between retries.
    // Even after 10 retries the backoff time is still only going to be a random
    // between 0 and 30s.
    this.MaxBackoff = 30000;

    // the base backoff time
    this.BaseBackoff = 1500;

    // reference to the currently executing request in case it hangs and we need
    // to abort it
    this.CurrentRequest = null;

    // flag used to indicate a already exited error receieved from Engine as part
    // of the postback response.
    this.AlreadyExitedError = false;

    // Set the commit interval
    this.CommCommitFrequency = rscpCfg.get("registrationToDeliver").Package.Properties.CommCommitFrequency;

    // make sure the commit interval is within sane bounds
    if (this.CommCommitFrequency < this.MinimumCommitFrequency) {
        this.CommCommitFrequency = this.MinimumCommitFrequency;
    }
    else if (this.CommCommitFrequency > this.MaxCommitFrequency) {
        this.CommCommitFrequency = this.MaxCommitFrequency;
    }

    // Ensure we haven't gone more than MaxNonCommitInterval time without any dirty data having been stored
    this.MaxNonCommitAttempts = parseInt(this.MaxNonCommitInterval / this.CommCommitFrequency, 10);

    // Number of times SaveData fired without any data to save
    this.NonCommitAttempts = 0;

    // Number of times SaveData fired
    this.CommitAttempts = 0;

    /* eslint-disable no-use-before-define */
    this.StartPostDataProcess = Communications_StartPostDataProcess;
    this.KillPostDataProcess = Communications_KillPostDataProcess;
    this.StartRequestTimeoutProcess = Communications_StartRequestTimeoutProcess;
    this.KillRequestTimeoutProcess = Communications_KillRequestTimeoutProcess;
    this.CancelPendingRequest = Communications_CancelPendingRequest;
    this.SaveDataWithBackoff = Communications_SaveDataWithBackoff;
    this.SaveData = Communications_SaveData;
    this.SaveDataNow = Communications_SaveDataNow;
    this.SaveDataOnExit = Communications_SaveDataOnExit;
    this.SaveDebugLog = Communications_SaveDebugLog;
    this.SendDataToServer = Communications_SendDataToServer;
    this.ResponseIndicatesAlreadyExited = Communications_ResponseIndicatesAlreadyExited;
    this.GetServerResponseErrorPresent = Communications_GetServerResponseErrorPresent;
    this.GetMatchPartsForRegex = Communications_GetMatchPartsForRegex;
    this.CheckServerResponse = Communications_CheckServerResponse;
    this.CallFailed = Communications_CallFailed;
    this.Disable = Communications_Disable;
    this.LogOnServer = Communications_LogOnServer;
    this.StartWatchProcess = Communications_StartWatchProcess;
    this.CheckComm = Communications_CheckComm;
    this.TrySendBeacon = Communications_TrySendBeacon;
    this.TryFetchThenBeacon = Communications_TryFetchThenBeacon;
    /* eslint-enable no-use-before-define */
}

(function () {
    function getStatusFromActivity (activity) {
        //
        // The course status for dispatch is a bit quirky. Completion is either "completed" or null, because it controls whether
        // or not SD.SetReachedEnd is called. Success can be "passed", "failed", or null. Score is either the normalized measure
        // multiplied by 100.0, or "unknown". (Why not null instead of "unknown"? No idea, but that's how it's always been.)
        //
        var status = {
            completion: null,
            success: null
        };

        if (activity.IsCompleted(null, false) !== RESULT_UNKNOWN && activity.IsCompleted(null, false) === true) {
            status.completion = "completed";
        }
        if (activity.IsSatisfied(null, false) !== RESULT_UNKNOWN) {
            if (activity.IsSatisfied(null, false) === true) {
                status.success = "passed";
            }
            else {
                status.success = "failed";
            }
        }

        if (activity.GetPrimaryObjective().MeasureStatus === true) {
            status.score = activity.GetPrimaryObjective().NormalizedMeasure * 100.0;
        }
        else {
            status.score = "unknown";
        }

        return status;
    }

    function lookaheadProvidesBetterStatus (root, lookahead) {
        if (
            (lookahead.success === "passed" && root.success !== "passed")
            || (lookahead.completion === "completed" && root.success !== "passed" && root.completion !== "completed")
            || (lookahead.score !== "unknown" && (root.score === "unknown" || lookahead.score > root.score) && (lookahead.success === "passed" || root.success !== "passed") && (lookahead.completion === "completed" || root.completion !== "completed"))
        ) {
            return true;
        }

        return false;
    }

    function getStatus () {
        var authoritativeSource = rscpCfg.get("dispatchStatusAuthoritativeSource"),
            status = getStatusFromActivity(Control.Activities.GetRootActivity()),
            lookaheadStatus,
            source;

        //
        // originally Dispatch had a more strict interpretation of the status returned from the root activity
        // and treated it authoritatively, but in some cases it was considered too authoritative, so we added
        // the ability to be more lenient, but this maintains the ability to be strict
        //
        if (authoritativeSource === "root_only") {
            source = "root activity";
        }
        else if (authoritativeSource === "root_preferred" && (status.completion !== null || status.success !== null || (status.score !== null && status.score !== "unknown"))) {
            source = "root activity";
        }
        else if (Control.Package.Properties.LookaheadSequencerMode === LOOKAHEAD_SEQUENCER_MODE_DISABLE) {
            Control.WriteDetailedLog("Lookahead sequencer disabled so not consulted for course status");
            source = "root activity";
        }
        else {
            Control.WriteDetailedLog("Lookahead sequencer being consulted for status");

            //
            // If the root activity didn't have anything, then we probably haven't rolled up yet. Check the look-ahead sequencer
            // for a possibly more up-to-date status. Otherwise, the remote LMS won't get any course status at all for SCORM 2004
            // content until Terminate is called, or until the player starts unloading because the window was closed. In the latter
            // case, it might be too late for the remote LMS to record the rolled-up status. Note: We handle this for SCORM 1.2
            // in the API implementation by immediately rolling up when lesson_status changes.
            //
            lookaheadStatus = getStatusFromActivity(Control.LookAheadSequencer.Activities.GetRootActivity());

            if (lookaheadStatus.completion === null && lookaheadStatus.success === null && (lookaheadStatus.score === null || lookaheadStatus.score === "unknown")) {
                Control.WriteDetailedLog("Lookahead sequencer doesn't have data (falling back to root activity)");
                source = "root activity";
            }
            else if (authoritativeSource === "lookahead_preferred") {
                Control.WriteDetailedLog("Lookahead sequencer is preferred and populated");
                status = lookaheadStatus;
                source = "lookahead sequencer";
            }
            else if (authoritativeSource === "root_preferred") {
                Control.WriteDetailedLog("Root activity doesn't have data (falling back to lookahead sequencer)");
                status = lookaheadStatus;
                source = "lookahead sequencer";
            }
            else if (authoritativeSource === "learner_preferred" && lookaheadProvidesBetterStatus(status, lookaheadStatus)) {
                Control.WriteDetailedLog("Best for learner comes from lookahead sequencer");
                status = lookaheadStatus;
                source = "lookahead sequencer";
            }
            else if (authoritativeSource === "learner_preferred") {
                Control.WriteDetailedLog("Lookahead sequencer doesn't have better data for learner (falling back to root activity)");
                source = "root activity";
            }
            else {
                Control.WriteDetailedLog("Unrecognized condition in determining course status (falling back to root activity)");
                source = "root activity";
            }
        }

        Control.WriteDetailedLog("Got Dispatch course status from " + source + " (" + authoritativeSource + ").");

        //
        // if a status for completion still hasn't been realized then we want to treat it as "incomplete" for Dispatch purposes
        // this is to make sure that the 3rd party LMS always receives some value for the Dispatch package, this was originally
        // done by Driver on the Dispatch side, but in certain cases that caused an issue, so instead explicitly pass incomplete
        // through the pipe and let the Dispatch package determine whether it should get set in the 3rd party LMS rather than
        // Driver determining it on its own
        //
        if (status.completion === null) {
            status.completion = "incomplete";
        }

        return status;
    }

    Communications.prototype = {
        SendDispatchMessagesIfNeeded: function (isLaunchState) {
            var status,
                trackedSessionTime = 0,
                reportedSessionTime = 0,
                allActivities = Control.Activities.ActivityList,
                allInteractions = [],
                i,
                j,
                act,
                sessionTime;

            if (typeof DispatchDriver === "undefined") {
                return;
            }

            isLaunchState = isLaunchState || false;

            status = getStatus();

            // Rollup session times across all deliverable activities...
            for (i = 0; i < allActivities.length; i += 1) {
                act = allActivities[i];
                if (act.IsDeliverable()) {
                    trackedSessionTime += ConvertIso8601TimeSpanToHundredths(act.RunTime.SessionTimeTracked);
                    reportedSessionTime += ConvertIso8601TimeSpanToHundredths(act.RunTime.SessionTime);

                    for (j = 0; j < act.RunTime.Interactions.length; j += 1) {
                        allInteractions.push(act.RunTime.Interactions[j]);
                    }
                }
            }
            sessionTime = trackedSessionTime > 0 ? trackedSessionTime : reportedSessionTime;
            if (sessionTime === null || isNaN(sessionTime)) {
                sessionTime = 0;
            }

            try {
                DispatchDriver.SetSummary(isLaunchState, status.completion, status.success, status.score, sessionTime);
            }
            catch (ex) {
                throw new Error("Failed to SetSummary on DispatchDriver: " + ex);
            }
            try {
                DispatchDriver.SetInteractions(isLaunchState, allInteractions);
            }
            catch (ex) {
                throw new Error("Failed to SetInteractions on DispatchDriver: " + ex);
            }
        },
        TryFetchKeepAlive: function (url, data) {
            // have to support fetch() and keepalive flag to continue
            if (typeof fetch !== "function" || ! ("keepalive" in new Request(""))) {
                return false;
            }

            try {
                fetch(
                    url,
                    {
                        method: "POST",
                        mode: "cors",
                        cache: "no-cache",
                        credentials: "include",
                        body: data,
                        keepalive: true
                    }
                ).then(function (response) {
                    Control.WriteDetailedLog("Exit postback using Fetch with KeepAlive finished with status " + response.status + ":" + response.statusText);
                }).catch(function (e) {
                    Control.WriteDetailedLog("Error making exit postback fetch request: " + e.message);
                });

                // We're making an assumption here that if we can fire this request, it
                // will complete successfully. Since we couldn't deal with a failure
                // anyway, there's no point in handling the Promise returned by the
                // fetch call.
                return true;
            }
            catch (e) {
                // This will likely never get hit, but we don't want any error to bubble up from this method
                Control.WriteDetailedLog("Error making exit postback fetch request: " + e.message);

                return false;
            }
        }
    };
}());

function Communications_StartWatchProcess () {
    if (this.Disabled) {
        return;
    }

    if (this.IntervalWatchFunctionID === "") {
        Control.WriteDetailedLog("Communications_StartWatchProcess");

        // Check after MaxNonCommitInterval to see if we have made comm
        this.IntervalWatchFunctionID = window.setInterval(function () {
            Control.Comm.CheckComm();
        }, this.MaxNonCommitInterval);
    }
}

function Communications_CheckComm () {
    if (this.Disabled) {
        return;
    }

    Control.WriteDetailedLog("Communications_CheckComm");
    if (this.CommitAttempts === 0) {
        // It has been 30 minutes and not a single attempt has been made to save the user's data
        Control.WriteDetailedLog("WARNING: GL-200 30 minutes passed without persisting any data");
        Cookies.set("GLError", "GL-200", {expires: 14});
    }
}

function Communications_StartPostDataProcess () {
    if (this.Disabled) {
        return;
    }
    if (this.IntervalFunctionID === "") {
        Control.WriteDetailedLog("Communications_StartPostDataProcess");

        this.IntervalFunctionID = window.setInterval(function () {
            Control.Comm.SaveData(false, false);
        }, this.CommCommitFrequency);
    }
}

function Communications_KillPostDataProcess () {
    if (this.Disabled) {
        return;
    }

    Control.WriteDetailedLog("Communications_KillPostDataProcess");
    if (this.IntervalFunctionID !== "") {
        window.clearInterval(this.IntervalFunctionID);
        this.IntervalFunctionID = "";
    }
}

function Communications_StartRequestTimeoutProcess () {
    if (this.Disabled) {
        return;
    }

    Control.WriteDetailedLog("Communications_StartRequestTimeoutProcess");
    if (this.RequestTimeoutIntervalID === "") {
        this.RequestTimeoutIntervalID = window.setInterval(function () {
            Control.Comm.CancelPendingRequest();
        }, this.PendingRequestTimeout);
    }
}

function Communications_KillRequestTimeoutProcess () {
    if (this.Disabled) {
        return;
    }

    Control.WriteDetailedLog("Communications_KillRequestTimeoutProcess");
    if (this.RequestTimeoutIntervalID !== "") {
        window.clearInterval(this.RequestTimeoutIntervalID);
        this.RequestTimeoutIntervalID = "";
    }
}

function Communications_CancelPendingRequest () {
    // readyState 4 is DONE
    if (this.CurrentRequest !== null && this.CurrentRequest.readyState !== 4) {
        // calling abort() on the request will trigger the error function for the
        // request to be triggered (see SendDataToServer method)
        this.CurrentRequest.abort();
    }
}

function Communications_SaveDataWithBackoff (isFinalExit) {
    var backoffTimeInMillis = 0;

    if (this.disabled) {
        return true;
    }

    if (! isFinalExit) {
        // We're using the "full jitter" algorithm from: https://www.awsarchitectureblog.com/2015/03/backoff.html
        //
        // because it is basically the best for work (calls) and competes with
        // decorrelated for completion time, with the gap closing the more clients
        // are competing. Seeing as this is attempting to fix a cloud (eg, lots of clients)
        // issue it seems to make sense
        backoffTimeInMillis = Math.random() * Math.min(this.MaxBackoff, this.BaseBackoff * 2 * this.FailedSubmissions);

        // the figures used above have been chosen somewhat strategically based
        // on the Jira wishing to backoff for up to 5s on cloud. Cloud defaults
        // to 2 retries so 1500*2*2 = 6000, which isn't far off. If the retries
        // are set really high we could backoff for up to 120s
    }
    setTimeout(
        function () {
            Control.Comm.SaveData(false, isFinalExit);
        },
        backoffTimeInMillis
    );
}

function Communications_SaveData (synchronous, isFinalExit) {
    var stateXml,
        success;

    if (this.Disabled) {
        return true;
    }

    try {
        this.CommitAttempts += 1;
        Control.WriteDetailedLog("Communications Save Data, synchronous=" + synchronous + ", isExit=" + isFinalExit + ", commitAttempt=" + this.CommitAttempts);

        this.SendDispatchMessagesIfNeeded();

        success = true;

        if (isFinalExit === true) {
            Control.WriteDetailedLog("About to save final data upon player exit, final exit calls = " + this.FinalExitCalls);

            Control.MarkDirtyDataPosted();

            stateXml = Control.GetXmlForDirtyData();

            this.SendDataToServer(synchronous, stateXml, true);
        }
        else if (Control.IsThereDirtyData()) {
            Control.WriteDetailedLog("Found Dirty Data to Save");

            Control.MarkDirtyDataPosted();
            stateXml = Control.GetXmlForDirtyData();

            success = this.SendDataToServer(synchronous, stateXml);
        }
        else {
            Control.WriteDetailedLog("No Dirty Data to Save");

            this.NonCommitAttempts += 1;
            if (this.NonCommitAttempts >= this.MaxNonCommitAttempts) {
                // It has been 30 minutes and we have not had any dirty data to save
                Control.WriteDetailedLog("WARNING: GL-100 " + ((this.CommCommitFrequency * this.MaxNonCommitAttempts) / 60000) + " minutes without commiting data");
                Cookies.set("GLError", "GL-100", {expires: 14});
            }
        }

        return success;
    }
    catch (e) {
        Control.WriteDetailedLogError("ERROR - Exception in Communications_SaveData, results may not have been posted: " + GetErrorDetailString(e));
        this.LogOnServer("Exception in Communications_SaveData, results may not have been posted", e);
        this.CallFailed();
    }
}

function Communications_SaveDataNow (endPeriodicCommit, synchronous) {
    var success = false;

    if (this.Disabled) {
        return true;
    }

    Control.WriteDetailedLog("Communications_SaveDataNow");

    // kill the interval call so we don't get into timing issues with this function being called simultaneously
    this.KillPostDataProcess();

    success = this.SaveData(synchronous);

    if (! endPeriodicCommit) {
        this.StartPostDataProcess();
    }

    return success;
}

function Communications_SaveDataOnExit () {
    if (this.Disabled) {
        return;
    }

    // kill the interval call so we don't get into timing issues with this function being called simultaneously

    Control.WriteDetailedLog("Communications_SaveDataOnExit");

    this.KillPostDataProcess();

    this.SaveData(true, true);
}

function Communications_SaveDebugLog () {
    var debugLogToSave,
        dataType,
        contentType;

    // Only send data if we've got some
    if (Debug.log.entries.length > 0) {
        if (rscpCfg.get("sendDebugLogsAsJson")) {
            debugLogToSave = JSON.stringify(Debug);
            dataType = "json";
            contentType = "application/json";
        }
        else {
            debugLogToSave = Debug.GetSerializedLog();
            dataType = "text";
            contentType = "text/xml";
        }

        // Do this synchronously because it's done upon exit and
        // the page we return to needs to have already gotten the logs
        $.ajax(
            {
                url: rscpCfg.get("debugLogPersistPage"),
                type: "POST",
                dataType: dataType,
                contentType: contentType,
                data: debugLogToSave,
                async: false
                // TODO: failure handler
            }
        );
    }
}

function Communications_TryFetchThenBeacon (resultPostbackPage, strXmlPostData) {
    if (! Control.Comm.TryFetchKeepAlive(resultPostbackPage, strXmlPostData)) {
        // Last resort, force use of sendBeacon
        Control.WriteDetailedLog("Fetch with KeepAlive failed, so trying to send exit postback using SendBeacon");

        // eslint-disable-next-line no-global-assign, no-native-reassign
        PREFER_SEND_BEACON = true;

        return Control.Comm.TrySendBeacon(resultPostbackPage, strXmlPostData);
    }

    Control.WriteDetailedLog("Exit postback using Fetch with KeepAlive was sent (this does not guarantee it was successful)");

    return true;
}

function Communications_SendDataToServer (synchronous, strXmlPostData, isExit) {
    var resultPostbackPage,
        isSuccessful;

    if (this.Disabled) {
        return true;
    }

    // for offline use
    if (typeof GetCurrentConfigurationJs !== "undefined") {
        localStorage.configurationJs = GetCurrentConfigurationJs();
        localStorage.runtimeXml = strXmlPostData;

        return true;
    }

    // this sequence number is (and is originally initialized to) the *last* sequence # for this registration,
    // and the server will ignore sequence numbers that are less than or equal to the current one,
    // so it must be incremented before sending
    this.SequenceNumber += 1;
    resultPostbackPage = MergeQueryStringParameters(rscpCfg.get("scormResultsPage"), "sequence=" + this.SequenceNumber);

    if (isExit) {
        resultPostbackPage = MergeQueryStringParameters(resultPostbackPage, "isExitScormPlayer=true");

        // All modern browsers but FireFox block XHR during page dismissal, and when FireFox did allow it the
        // synchronous call caused timing problems if a lagging api.terminate() call came in behind it,
        // so if we're unloading just skip straight to Fetch with KeepAlive followed by SendBeacon.
        if (Control.DeliverFramesetUnloadEventCalled) {
            Control.WriteDetailedLog("Page is unloading, trying Fetch with KeepAlive");

            return this.TryFetchThenBeacon(resultPostbackPage, strXmlPostData);
        }

        if (this.TrySendBeacon(resultPostbackPage, strXmlPostData)) {
            // successfully queued for send, browser is responsible for retrying so report success,
            // particularly since we only try this when exiting and have few options on failure
            return true;
        }
    }

    // make sure synchronous is a valid boolean (use the NOT operator twice)
    synchronous = !! synchronous;

    Control.WriteDetailedLog("In SendDataToServer, synchronous=" + synchronous + ", strPostData=" + strXmlPostData);

    // A couple notes about the .ajax call below.  Return type is text because although the string is xml, we
    // expect to manually parse the string rather than view it as an xml object.  The content type has to
    // be explicitly set to text/xml or the java-side code won't read the input stream properly
    isSuccessful = true;

    // while a request is pending (eg, we haven't had a response from the server and it hasn't timed out)
    // we cancel the commit frequency to reduce possible server spamming of requests if, for example, the db is
    // down and the request has hung (as can happen when using tomcat dbcp)

    // This won't do any harm should the process have already been halted (eg, by SaveDataOnExit())
    this.KillPostDataProcess();
    this.StartRequestTimeoutProcess();

    this.CurrentRequest = $.ajax(
        {
            type: "POST",
            url: resultPostbackPage,
            cache: false,
            dataType: "text",
            contentType: "text/xml",
            data: strXmlPostData,
            xhrFields: {
                withCredentials: true
            },
            async: ! synchronous,
            success: function (response) {
                // success here doesn't mean all was well, that is determined by isSuccessful below.
                // This "success" just means we got a 200 back as the ERROR log below indicates.
                Control.WriteDetailedLog("Server Responded With:" + response);

                // we've got a response back, so kill the interval timer that is waiting for it to timeout
                Control.Comm.KillRequestTimeoutProcess();
                Control.Comm.CurrentRequest = null;

                isSuccessful = Control.Comm.CheckServerResponse(response, true, synchronous, isExit);

                // We successfully updated the registration runtime state on the server. Update the 'LastState'
                // object if we're using delta based record results so we only send dirty data in the future
                if (isSuccessful && rscpCfg.get("useDeltaRecordResultsPayload") === true) {
                    Control.UpdateLastState(strXmlPostData);
                }

                // CheckServerResponse will initiate a retry if '<error present="true">' because we
                // supply true for retryFailures above. Therefore we wait until after to restart the
                // commit frequency timer:
                // If we are exiting, don't restart this process as it is shut down specifically by
                // SaveDataOnExit()
                if (! isExit) {
                    Control.Comm.StartPostDataProcess();
                }
            },
            error: function (req) {
                var matchParts,
                    canTryAgain,
                    checkServerResponseSuccess;

                Control.WriteDetailedLogError("ERROR - Server side error occurred when saving state data, HTTP response not 200 (OK). Status: " + req.status);

                // we don't ever restart the commit frequency timer watchdog in this function.
                // This is because we will either keep trying until we get a success at which
                // point it is restarted in success above or the player quits, so there is no
                // point restarting it as it will just be killed anyway by SaveDataOnExit

                Control.Comm.KillRequestTimeoutProcess();
                Control.Comm.CurrentRequest = null;

                // Handling case where our call to XMLHttpRequest was blocked for some reason,
                // so that we can fall back to other mechanisms. We're restricting this to
                // synchronous exit postbacks only, as the primary intention is to deal with
                // Chrome's disabling of sync XHR during page dismissal, which triggers a NetworkError.
                // This status = 0 check will pick up any sort of error that happens that prevents the
                // request from being sent, so may be hit by other types of Network Errors and such.
                // NOTE: The case of page dismissal is largely covered by the similar `isExit` block above,
                // but we're keeping this here for network errors not involved in page dismissal
                if (isExit && synchronous && req.status === 0) {
                    Control.WriteDetailedLog("Network Error on synchronous exit, so trying to send exit postback using Fetch with KeepAlive. Sync request failure message: " + req.statusText);

                    return this.TryFetchThenBeacon(resultPostbackPage, strXmlPostData);
                }

                // even if the status is 500 we may have response XML from Engine. If so, we'll
                // use the same logic as with "success" above to process it. Otherwise,
                // we'll treat it as if Engine hasn't responded at all....
                matchParts = Control.Comm.GetServerResponseErrorPresent(req.responseText);
                if (matchParts === null || matchParts.length !== 2) {
                    canTryAgain = Control.Comm.CallFailed();
                    if (canTryAgain) {
                        if (synchronous) {
                            isSuccessful = Control.Comm.SaveData(synchronous, isExit);
                        }
                        else {
                            // SaveDataWithBackoff is inherently asynchronous due to the setTimeout
                            // for the backoff. Therefore we don't use it if we are synchronous. For
                            // the same reason, it doesn't return a success flag
                            Control.Comm.SaveDataWithBackoff(isExit);
                        }
                    }
                    else {
                        isSuccessful = false;
                    }
                }
                else {
                    isSuccessful = false;

                    checkServerResponseSuccess = Control.Comm.CheckServerResponse(req.responseText, true, synchronous, isExit);
                    if (checkServerResponseSuccess) {
                        Control.WriteDetailedLog("SendDataToServer received error callback but CheckServerResponse returned success=true: " + req.responseText);
                    }
                }
            }
        }
    );

    return isSuccessful;
}

function Communications_GetMatchPartsForRegex (regex, sourceText) {
    if (typeof sourceText === "undefined" || sourceText === null) {
        return null;
    }

    sourceText = String(sourceText);
    if (sourceText.trim() === "") {
        return null;
    }

    return sourceText.match(regex);
}

function Communications_GetServerResponseErrorPresent (responseText) {
    return this.GetMatchPartsForRegex(/<error present="(true|false)">/, responseText);
}

function Communications_ResponseIndicatesAlreadyExited (responseText) {
    // No match = false, match = true
    return this.GetMatchPartsForRegex(/<alreadyexitederror/, responseText) !== null;
}

function Communications_CheckServerResponse (serverResponse, retryFailures, synchronous, isExit) {
    var success,
        matchParts,
        callSucceeded,
        canTryAgain;

    if (this.Disabled) {
        return true;
    }

    /*
    Expected response format
        <?xml version="1.0"?>
        <lmsresponse>
        <error present="true/false">
        <alreadyexitederror>
        <source>error source</source>
        <description>error description</description>
        </error>
        </lmsresponse>
    */
    // <alreadyexitederror> is optional

    matchParts = this.GetServerResponseErrorPresent(serverResponse);

    if (matchParts === null || matchParts.length !== 2) {
        Control.WriteDetailedLogError("ERROR - Invalid server response received from the LMS.");
        success = false;
    }
    else {
        callSucceeded = matchParts[1] === "false";
        if (callSucceeded === false) {
            Control.WriteDetailedLogError("ERROR - LMS was unable to successfully save state date, see the LMS response for specific error information. Server Response=" + String(serverResponse));
            success = false;
            this.AlreadyExitedError = this.ResponseIndicatesAlreadyExited(serverResponse);
        }
        else {
            success = true;
        }
    }

    if (success === false) {
        canTryAgain = this.CallFailed();

        if (retryFailures && canTryAgain) {
            if (synchronous) {
                this.SaveData(synchronous, isExit);
            }
            else {
                this.SaveDataWithBackoff(isExit);
            }
        }
    }
    else {
        this.FailedSubmissions = 0;
        Control.MarkPostedDataClean();
    }

    return success;
}

function Communications_CallFailed () {
    if (this.Disabled) {
        return false;
    }

    this.FailedSubmissions += 1;
    Control.MarkPostedDataDirty();

    Control.WriteDetailedLog("Communications Call Failed, Failed Submissions=" + this.FailedSubmissions);

    if (this.FailedSubmissions >= rscpCfg.get("registrationToDeliver").Package.Properties.CommMaxFailedSubmissions || this.AlreadyExitedError) {
        this.KillPostDataProcess();

        // Only show the error if we're not already on our way down
        if (! Control.DeliverFramesetUnloadEventCalled) {
            Control.DisplayError("A fatal error has occurred, communication with the server has been lost.");
        }

        // Adding a comment here as a note for possible further work.....

        // Is this REALLY what we want to happen? If, for example, you have a number of retries
        // set as 2, which may or may not be reasonable (it's the default on cloud for example),
        // then the 2nd retry will happen immediately and the player will quit. That seems a bit
        // off, especially if the individual requests fail quickly.

        // Also, this approach basically guarantees anyone with dirty data will lose data if their
        // retry limit is exceeded. A better approach may be to notify the user at this point and
        // give them the option of quitting or carrying on knowing their data is not saving (we
        // would of course have to let them know when/if it does eventually successfully save).
        // We could/should then apply a backoff algorithm to the retries rather than just trying
        // on the commit frequency, which could be set low.

        // Go ahead and exit the player to remove the possibility of somebody continuing with the
        // course and not having their data persisted.
        Control.FatalErrorEncountered = true;

        if (Control.Package.Properties.PlayerLaunchType === LAUNCH_TYPE_FRAMESET) {
            Control.HandleRedirectIfRequired(window);
        }
        else {
            // Protect against case where the top level window is killed/closed.
            if (window.opener && window.opener !== null && window.opener.closed === false) {
                Control.HandleRedirectIfRequired(window.opener);
            }
            window.close();
        }

        return false;
    }

    return true;
}

function Communications_Disable () {
    this.Disabled = true;
}

function Communications_LogOnServer (msg, jsErrorObject) {
    var msgToSend = msg,
        regId = rscpCfg.get("registrationToDeliver").Id;

    // this if statement checks if we are offline; if we are, then don't attempt sending logs to server
    if (typeof GetCurrentConfigurationJs !== "undefined") {
        return;
    }

    if (jsErrorObject !== null) {
        msgToSend = msg + GetErrorDetailString(jsErrorObject);
    }

    // We need a registration for the all the ClientLogProcessor stuff to work.
    if (regId !== "") {
        // Log this js error server side
        $.ajax(
            {
                type: "POST",
                url: rscpCfg.get("serverLoggerPage"),
                cache: false,
                dataType: "text",
                data: {
                    msg: regId + " - " + msgToSend
                },
                async: false
            }
        );
    }
}

// Try to use navigator.sendBeacon (if configured to do so and if the feature is detected)
// Even in browsers that support this feature, the data may be rejected as "too large", in which case
// sendBeacon() will return false, so this function will pass that false along to get the same behavior
// as if sendBeacon() was not supported at all, or not configured.
// If sendBeacon() returns true, the browser is required to send the data even if doing so requires making
// retry attempts, so we'll consider it sent.
function Communications_TrySendBeacon (url, data) {
    if (PREFER_SEND_BEACON && window.navigator && typeof window.navigator.sendBeacon === "function") {
        return window.navigator.sendBeacon(url, data);
    }

    return false;
}
